import { TextArea, TextInput } from '@frontend/components/basic-forms';
import { Spinner } from '@frontend/components/common';
import { ICONS } from '@frontend/icons';
import { CommonMessage } from '@frontend/lang';
import { FaCheck, FaXmark } from 'react-icons/fa6';
import { LuFile, LuPaperclip, LuTrash2, LuUpload } from 'react-icons/lu';
import { FormattedMessage, useIntl } from 'react-intl';

import { useWizardModalScreenNavigation } from '../../wizard-modal-navigation.context';
import useEnterTrackingNumberAndShippingNotes from './enter-tracking-number-and-shipping-notes.controller';

const ID = 'enter-tracking-number-and-shipping-notes';

const formatFileSize = (size: number | null): string | null => {
    if (size === null) return null;
    if (size < 1024) return `${size} B`;
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

const EnterTrackingNumberAndShippingNotes = (props: { wizard_id: string }) => {
    const intl = useIntl();
    const viewProps = useEnterTrackingNumberAndShippingNotes(props);
    const removeAttachmentLabel = intl.formatMessage({
        id: 'wizard.enter-tracking-number-and-shipping-notes.remove-attachment',
        defaultMessage: 'Remove attachment'
    });

    useWizardModalScreenNavigation({
        onContinue: viewProps.onSubmit,
        continueDisabled: viewProps.submitDisabled
    });

    return (
        <div
            id={ID}
            className='flex w-full flex-col gap-3'>
            <div className='flex items-start justify-start self-stretch'>
                <div className='text-foreground-default justify-start text-base font-medium'>{CommonMessage.FORMS.PROVIDE_THE_FOLLOWING}</div>
            </div>

            <div
                data-state='Open'
                className='outline-border-default inline-flex flex-col items-start justify-start gap-5 self-stretch rounded-xl p-4 outline-1'>
                <div className='inline-flex items-center justify-between self-stretch'>
                    <div className='flex items-center justify-start gap-4'>
                        <div className='outline-border-default relative flex size-12 items-center justify-center rounded-full outline-2'>
                            {viewProps.trackingNumber ? (
                                <FaCheck className='bg-status-success text-background-surface absolute top-0 right-0 size-4 rounded-full p-0.5' />
                            ) : (
                                <FaXmark className='bg-brand-muted text-background-surface absolute top-0 right-0 size-4 rounded-full p-0.5' />
                            )}
                            <ICONS.ENTITY.TRANSACTION className='size-1/2' />
                        </div>
                        <div className='inline-flex flex-col items-start justify-center gap-1'>
                            <div className='text-foreground-default justify-start text-base font-semibold'>
                                {CommonMessage.OBJECTS.TRANSACTION.TRACKING_NUMBER}
                            </div>
                        </div>
                    </div>
                </div>

                <TextInput
                    className='w-full'
                    value={viewProps.trackingNumber || ''}
                    onChange={viewProps.setTrackingNumber}
                />
            </div>

            <div
                data-state='Open'
                className='outline-border-default inline-flex flex-col items-start justify-start gap-5 self-stretch rounded-xl p-4 outline-1'>
                <div className='inline-flex items-center justify-between self-stretch'>
                    <div className='flex items-center justify-start gap-4'>
                        <div className='outline-border-default relative flex size-12 items-center justify-center rounded-full outline-2'>
                            {viewProps.shippingNotes ? (
                                <FaCheck className='bg-status-success text-background-surface absolute top-0 right-0 size-4 rounded-full p-0.5' />
                            ) : (
                                <FaXmark className='bg-brand-muted text-background-surface absolute top-0 right-0 size-4 rounded-full p-0.5' />
                            )}
                            <ICONS.BUTTON.CHANGE_PASSWORD className='size-1/2' />
                        </div>
                        <div className='inline-flex flex-col items-start justify-center gap-1'>
                            <div className='text-foreground-default justify-start text-base font-semibold'>
                                {CommonMessage.OBJECTS.TRANSACTION.SHIPPING_NOTES}
                            </div>
                        </div>
                    </div>
                </div>

                <TextArea
                    className='w-full'
                    value={viewProps.shippingNotes ?? ''}
                    onChange={(value) => viewProps.setShippingNotes(value)}
                    label={CommonMessage.OBJECTS.TRANSACTION.SHIPPING_NOTES}
                    submitted={false}
                />
            </div>

            <div
                data-state='Open'
                className='outline-border-default inline-flex flex-col items-start justify-start gap-5 self-stretch rounded-xl p-4 outline-1'>
                <div className='inline-flex items-center justify-between self-stretch'>
                    <div className='flex items-center justify-start gap-4'>
                        <div className='outline-border-default relative flex size-12 items-center justify-center rounded-full outline-2'>
                            {viewProps.attachments.length > 0 ? (
                                <FaCheck className='bg-status-success text-background-surface absolute top-0 right-0 size-4 rounded-full p-0.5' />
                            ) : (
                                <FaXmark className='bg-brand-muted text-background-surface absolute top-0 right-0 size-4 rounded-full p-0.5' />
                            )}
                            <LuPaperclip className='size-1/2' />
                        </div>
                        <div className='inline-flex flex-col items-start justify-center gap-1'>
                            <div className='text-foreground-default justify-start text-base font-semibold'>
                                <FormattedMessage
                                    id='wizard.enter-tracking-number-and-shipping-notes.attachments'
                                    defaultMessage='Attachments'
                                />
                            </div>
                            <div className='text-brand-muted text-sm'>
                                <FormattedMessage
                                    id='wizard.enter-tracking-number-and-shipping-notes.attachments-description'
                                    defaultMessage='Add images, documents, or other files to this transaction.'
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {viewProps.isLoadingAttachments ? (
                    <div className='flex min-h-12 w-full items-center justify-center'>
                        <Spinner className='size-5' />
                    </div>
                ) : (
                    viewProps.attachments.length > 0 && (
                        <div className='border-border-default flex w-full flex-col divide-y rounded-xl border'>
                            {viewProps.attachments.map((attachment) => {
                                const isRemoving = viewProps.removingAttachmentIds.has(attachment.id);
                                return (
                                    <div
                                        key={attachment.id}
                                        className='flex min-h-14 items-center gap-3 px-3 py-2'>
                                        <LuFile className='text-brand-muted size-5 shrink-0' />
                                        <div className='min-w-0 flex-1'>
                                            <div className='text-foreground-default truncate text-sm font-medium'>{attachment.name}</div>
                                            {formatFileSize(attachment.size) && (
                                                <div className='text-brand-muted text-xs'>{formatFileSize(attachment.size)}</div>
                                            )}
                                        </div>
                                        <button
                                            type='button'
                                            disabled={isRemoving || viewProps.isUploadingAttachments}
                                            onClick={() => void viewProps.removeAttachment(attachment.id)}
                                            aria-label={removeAttachmentLabel}
                                            title={removeAttachmentLabel}
                                            className='text-brand-muted hover:text-status-danger disabled:text-brand-muted flex size-9 shrink-0 items-center justify-center rounded-md disabled:cursor-not-allowed'>
                                            {isRemoving ? <Spinner className='size-4' /> : <LuTrash2 className='size-4' />}
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    )
                )}

                {viewProps.attachmentError && (
                    <div className='text-status-danger text-sm font-medium'>
                        {viewProps.attachmentError === 'load' && (
                            <FormattedMessage
                                id='wizard.enter-tracking-number-and-shipping-notes.attachments-load-error'
                                defaultMessage='The attachments could not be loaded.'
                            />
                        )}
                        {viewProps.attachmentError === 'upload' && (
                            <FormattedMessage
                                id='wizard.enter-tracking-number-and-shipping-notes.attachments-upload-error'
                                defaultMessage='One or more attachments could not be uploaded.'
                            />
                        )}
                        {viewProps.attachmentError === 'delete' && (
                            <FormattedMessage
                                id='wizard.enter-tracking-number-and-shipping-notes.attachments-delete-error'
                                defaultMessage='The attachment could not be removed.'
                            />
                        )}
                    </div>
                )}

                <label
                    htmlFor={`${ID}-attachments`}
                    className={`border-brand-primary text-brand-primary inline-flex min-h-11 items-center justify-center gap-2 rounded-md border px-4 py-2 text-sm font-medium ${
                        viewProps.isLoadingAttachments || viewProps.isUploadingAttachments
                            ? 'cursor-not-allowed opacity-50'
                            : 'hover:bg-brand-light cursor-pointer'
                    }`}>
                    <input
                        ref={viewProps.fileInputRef}
                        id={`${ID}-attachments`}
                        type='file'
                        multiple
                        disabled={viewProps.isLoadingAttachments || viewProps.isUploadingAttachments}
                        className='hidden'
                        onChange={(event) => void viewProps.addAttachments(Array.from(event.target.files ?? []))}
                    />
                    {viewProps.isUploadingAttachments ? <Spinner className='size-4' /> : <LuUpload className='size-4' />}
                    <FormattedMessage
                        id='wizard.enter-tracking-number-and-shipping-notes.add-attachments'
                        defaultMessage='Add attachments'
                    />
                </label>
            </div>
        </div>
    );
};

export default EnterTrackingNumberAndShippingNotes;
